import React, { type FormEvent, type ReactElement, type ReactNode } from "react";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ndd/ui";
import {
  progressLabel,
  type ActionProblem,
  type ActionProgress,
} from "../../../lib/rd-schedule-actions";
import { ActionError } from "./action-parts";
import type { ActionOptionsState } from "./use-action-options";

// NO-876. The frame every appointment-action dialog sits in: the existing
// Dialog, a title and one plain line under it, a form so Enter submits, and a
// footer whose confirm button names the result.

export function ActionDialogFrame({
  open,
  onOpenChange,
  pending,
  title,
  subtitle,
  role,
  initialFocusId,
  holdEscape,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending: boolean;
  title: string;
  subtitle: ReactNode;
  role?: "alertdialog";
  /** The control that takes focus on open; a destructive dialog starts on its safe button. */
  initialFocusId?: string;
  /** True while Escape belongs to something inside the dialog, such as an open results list. */
  holdEscape?: () => boolean;
  children: ReactNode;
}): ReactElement {
  // A change already being sent is not interrupted by closing.
  const guarded = (next: boolean) => {
    if (!next && pending) return;

    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={guarded}>
      <DialogContent
        role={role}
        className={cn(
          "max-h-[92vh] max-w-[560px] overflow-y-auto rounded-[22px]",
          // The shared close X, sized like the details panel's: a 32px target.
          // A destructive alert has no X; its safe button is the way out.
          "[&>button:last-child]:grid [&>button:last-child]:size-8 [&>button:last-child]:place-items-center [&>button:last-child]:rounded-[10px] [&>button:last-child]:text-muted-strong [&>button:last-child]:opacity-100 [&>button:last-child]:hover:bg-accent [&>button:last-child>svg]:size-[18px]",
          role === "alertdialog" && "[&>button:last-child]:hidden"
        )}
        onEscapeKeyDown={(event) => {
          if (holdEscape?.()) event.preventDefault();
        }}
        onOpenAutoFocus={(event) => {
          const target = initialFocusId
            ? document.getElementById(initialFocusId)
            : null;

          if (!target) return;

          event.preventDefault();
          target.focus();
        }}
      >
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="text-heading-sm font-bold leading-title">
            {title}
          </DialogTitle>
          <DialogDescription asChild>
            <div>{subtitle}</div>
          </DialogDescription>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function ActionForm({
  onSubmit,
  problem,
  back,
  confirm,
  children,
}: {
  onSubmit: () => void;
  problem: ActionProblem | null;
  back: { id?: string; label: string; onClick: () => void };
  confirm: {
    label: string;
    variant?: "press" | "destructive";
    pending: boolean;
    /** Where the action stands while it is pending. */
    progress?: ActionProgress;
    /** The last try did not finish; confirming sends it again. */
    retrying?: boolean;
    /** Part of the change went through; confirming again would repeat it. */
    stale?: boolean;
    disabled?: boolean;
  };
  children: ReactNode;
}): ReactElement {
  const handle = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!confirm.pending && !confirm.disabled && !confirm.stale) onSubmit();
  };

  return (
    <form className="flex flex-col gap-5" onSubmit={handle} noValidate>
      {children}
      <ActionError problem={problem} />
      {/* Stacked on a phone, the two buttons run full width; side by side above. */}
      <DialogFooter className="gap-2.5 sm:items-center">
        <Button
          id={back.id}
          type="button"
          variant="utility"
          disabled={confirm.pending}
          onClick={back.onClick}
        >
          {back.label}
        </Button>
        <Button
          type="submit"
          variant={confirm.variant ?? "press"}
          shape={confirm.variant === "destructive" ? "default" : "pill"}
          disabled={confirm.disabled || confirm.pending || confirm.stale}
          aria-busy={confirm.pending}
        >
          {confirm.pending
            ? progressLabel(confirm.progress ?? "saving")
            : confirm.retrying
              ? "Retry"
              : confirm.label}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** What the dialog shows while its options read is loading or has failed. */
export function OptionsStatus({
  state,
  onRetry,
  onClose,
}: {
  state: ActionOptionsState;
  onRetry: () => void;
  onClose: () => void;
}): ReactElement | null {
  if (state.status === "ready") return null;

  if (state.status === "loading") {
    return (
      <p role="status" className="text-body-lg text-muted-foreground">
        Loading this visit…
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p role="alert" className="text-body-lg text-danger">
        This visit did not load. Try again, or close this and reopen the visit.
      </p>
      <DialogFooter>
        <Button type="button" variant="utility" onClick={onClose}>
          Close
        </Button>
        <Button type="button" variant="press" shape="pill" onClick={onRetry}>
          Try again
        </Button>
      </DialogFooter>
    </div>
  );
}
